import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { secureDownload, getDocPackKey, getRiskRegisterKey } from '@/lib/secureDownload';
import { supabase } from '@/integrations/supabase/client';
import { trackDocPackPurchase } from '@/lib/analytics';

/**
 * Listens for Lemon Squeezy overlay checkout success events
 * and auto-downloads the Document Pack + Risk Register via secure endpoint.
 * Also sends a thank-you email with links to the Account page.
 */
export function useLemonSqueezyCheckoutSuccess() {
  const { language } = useLanguage();

  useEffect(() => {
    const sendThankYouEmail = async (productType: 'doc-pack' | 'pro-subscription') => {
      try {
        const session = await supabase.auth.getSession();
        const email = session.data.session?.user?.email;
        if (!email) return;

        const productName = productType === 'doc-pack'
          ? 'ISO 27001 Document Pack'
          : 'Pro Subscription';

        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        await fetch(`https://${projectId}.supabase.co/functions/v1/send-thank-you-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session?.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            email,
            productName,
            productType,
            language,
          }),
        });
      } catch (err) {
        console.error('Failed to send thank-you email:', err);
      }
    };

    const waitForWordPackAccess = async (maxAttempts = 15): Promise<boolean> => {
      for (let i = 0; i < maxAttempts; i++) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('has_word_pack')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (profile?.has_word_pack) return true;
        await new Promise(r => setTimeout(r, 2000));
      }
      return false;
    };

    const triggerDocPackDownloads = async (
      orderId?: string,
      total?: number,
      currency?: string,
    ) => {
      // GA4: fire purchase only after Lemon Squeezy confirms the payment.
      // transaction_id (LS order id) dedupes if the event fires twice.
      if (orderId) {
        trackDocPackPurchase(orderId, total ?? 99, currency);
      } else {
        trackDocPackPurchase(`docpack-${Date.now()}`, total ?? 99, currency);
      }

      toast.success('Thank you for your purchase! Preparing your downloads...');

      const hasAccess = await waitForWordPackAccess();
      if (!hasAccess) {
        toast.info('Your files are being prepared. Please download them from your Account page.');
        sendThankYouEmail('doc-pack');
        return;
      }

      toast.success('Your downloads are starting...');
      await secureDownload(getDocPackKey(language));
      setTimeout(() => {
        secureDownload(getRiskRegisterKey(language));
      }, 1500);

      sendThankYouEmail('doc-pack');
    };

    const triggerProActivation = () => {
      toast.success('Welcome to Pro! Your subscription is active.');
      sendThankYouEmail('pro-subscription');
    };

    // Listen for postMessage events from Lemon Squeezy overlay iframe
    const handleMessage = (e: MessageEvent) => {
      // LS sends messages from its overlay iframe
      if (typeof e.data !== 'string') return;
      
      let parsed: any;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        return;
      }

      console.log('[LS Event]', parsed?.event, parsed);

      // Lemon Squeezy fires "Checkout.Success" on completed purchase
      if (parsed?.event === 'Checkout.Success') {
        const orderData = parsed?.data?.order;
        const firstItem = orderData?.attributes?.first_order_item || orderData?.first_order_item || {};
        const variantName = (firstItem.variant_name || '').toLowerCase();
        const productNameStr = (firstItem.product_name || '').toLowerCase();
        const isSubscription = variantName.includes('pro') ||
          productNameStr.includes('pro') ||
          firstItem.is_subscription === true;

        if (isSubscription) {
          triggerProActivation();
        } else {
          const orderId = String(
            orderData?.id ??
            orderData?.attributes?.identifier ??
            orderData?.attributes?.order_number ??
            ''
          );
          const totalCents = Number(orderData?.attributes?.total ?? 0);
          const total = totalCents > 0 ? totalCents / 100 : undefined;
          const currency = orderData?.attributes?.currency;
          triggerDocPackDownloads(orderId || undefined, total, currency);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Also set up via the official Setup API as a secondary method
    const setupLS = () => {
      if (typeof window !== 'undefined' && (window as any).LemonSqueezy) {
        (window as any).LemonSqueezy.Setup({
          eventHandler: (event: { event: string; data?: any }) => {
            console.log('[LS Setup Event]', event.event, event);
            if (event.event === 'Checkout.Success') {
              const firstItem = event.data?.order?.attributes?.first_order_item || {};
              const variantName = (firstItem.variant_name || '').toLowerCase();
              const productNameStr = (firstItem.product_name || '').toLowerCase();
              const isSubscription = variantName.includes('pro') ||
                productNameStr.includes('pro') ||
                firstItem.is_subscription === true;

              if (isSubscription) {
                triggerProActivation();
              } else {
                const orderData = event.data?.order;
                const orderId = String(
                  orderData?.id ??
                  orderData?.attributes?.identifier ??
                  orderData?.attributes?.order_number ??
                  ''
                );
                const totalCents = Number(orderData?.attributes?.total ?? 0);
                const total = totalCents > 0 ? totalCents / 100 : undefined;
                const currency = orderData?.attributes?.currency;
                triggerDocPackDownloads(orderId || undefined, total, currency);
              }
            }
          },
        });
      }
    };

    setupLS();
    const timer = setTimeout(setupLS, 2000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };
  }, [language]);
}
